import React, { useEffect, useState } from "react";
import { useReportAutoRun } from "hooks/useReportAutoRun/useReportAutoRun";
import { useLocation } from "react-router-dom";

import { downloadCsv, formatDate, openPrintWindow } from "helpers/Helpers";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { MOBILE_WIDTH, TABLET_WIDTH } from "helpers/ScreenSizes";

import { GetReport } from "api/rpc/2024-04/facilityAdmin/report/report";
import { GetVendor } from "api/rpc/2024-04/facilityAdmin/product/vendor";
import { GetRegisters } from "api/rpc/2024-04/facilityAdmin/facility/register/register";
import { GetDepartments } from "api/rpc/2024-04/facilityAdmin/facility/department";
import { StatusCode } from "api/protocols";
import { IConfiguration } from "api/rpc/clientAdmin/client/report";

import Page from "components/page/Page";
import Card from "components/card/Card";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import CompareSalesByDateTable from "../tables/CompareSalesByDateTable";
import { IRegister } from "../../Admin";
import { useTranslation } from "react-i18next";

import "../report.scss";
import { useAppDispatch } from "hooks/redux";
import { showError, showSuccess } from "redux/actions/ui";
import ReportEmailModal from "components/reportEmailModal/ReportEmailModal";
import moment from "moment";
import { IDepartments } from "redux/reducers/models/product";
import { useReportRun } from "../useReportRun";

interface IReportState {
  id: number;
  title: string;
  code: string;
  dateRangeStart: Date;
  dateRangeEnd: Date;
  singleDate: Date;
  configurations: Array<IConfiguration>;
  date_range: Array<string>;
  date: string;
  facilityOptions: Array<{ label: string; onClick: () => void }>;
  allVendors: any;
  vendor_ids: Array<number>;
  allRegisters: IRegister[];
  register_ids: Array<number>;
  email: string;
  queue_after_days: number;
  allDepartments: IDepartments[];
  department_ids: number[];
  category_ids: number[];
  subcategory_ids: number[];
  compare_date_range: Array<string>;
  compare_date_range_start: Date;
  compare_date_range_end: Date;
  [key: string]: any;
}

interface IReportDataState {
  headings: any;
  rows: any;
  columns: Array<Record<string, any>>;
  tableDisplay: boolean;
  description: string;
  compileDate: string;
}

export default function CompareSalesByDate() {
  const windowSize = useWindowSize();
  const location = useLocation();
  const { t } = useTranslation();
  const routeName = location.pathname.split("/");
  const code = routeName[routeName.length - 1];
  const GetReportRun = useReportRun();
  const dispatch = useAppDispatch();

  function formatDateRange(startingDate: Date, endingDate: Date) {
    return [formatDate(startingDate), formatDate(endingDate) + " 23:59:59"];
  }

  const today = new Date();

  const [state, setState] = useState<IReportState>({
    dateRangeStart: today,
    dateRangeEnd: today,
    singleDate: today,
    id: null,
    title: "",
    code: "",
    configurations: [],
    date_range: formatDateRange(today, today),
    date: formatDate(today),
    facilityOptions: [],
    allVendors: null,
    vendor_ids: [],
    allRegisters: null,
    register_ids: [],
    email: "",
    queue_after_days: 14,
    allDepartments: null,
    department_ids: null,
    category_ids: null,
    subcategory_ids: null,
    compare_date_range: formatDateRange(today, today),
    compare_date_range_start: today,
    compare_date_range_end: today,
  });

  const [reportData, setReportData] = useState<IReportDataState>({
    headings: [],
    rows: [],
    columns: [],
    tableDisplay: false,
    description: "",
    compileDate: "",
  });

  useEffect(() => {
    void loadReport();
    void getVendors();
    void getRegisters();
    void getDepartments();
  }, []);
  useReportAutoRun({
    isReady: state.id != null,
    reportId: state.id,
    setState,
    run: () => {
      void getReport(state.email);
    },
  });


  async function loadReport() {
    const reportRes = await GetReport({ code: code }, true);
    if (reportRes.status !== StatusCode.OK) {
      return;
    }

    const report = reportRes.data?.[0];
    if (!report) {
      dispatch(showError(t("secure.facility.reports.reports.002")));
      return;
    }

    const sortedConfigurations = [...(report.configurations ?? [])].sort((prev, next) => prev.position - next.position);

    setState(prev => ({
      ...prev,
      id: report.id,
      title: report.title,
      code: report.code,
      queue_after_days: report.queue_after_days,
      configurations: sortedConfigurations,
    }));
  }

  async function getReport(email: string) {
    const reportRes = await GetReportRun(
      {
        report_id: state.id,
        date_range: state.date_range,
        vendor_ids: state.vendor_ids,
        register_ids: state.register_ids,
        department_ids: state.department_ids,
        category_ids: state.category_ids,
        subcategory_ids: state.subcategory_ids,
        compare_date_range: state.compare_date_range,
        email,
      },
      true,
    );

    if (reportRes.status !== StatusCode.OK) {
      dispatch(showError(reportRes.message));
      return;
    }

    if (reportRes.message) {
      dispatch(showSuccess(reportRes.message));
    }

    const compileDate = reportRes.data?.compiled_at ? reportRes.data?.compiled_at.concat("Z") : "";

    setReportData(prev => ({
      ...prev,
      rows: reportRes.data.rows,
      columns: reportRes.data.columns,
      description: reportRes.data?.description ?? "",
      compileDate,
    }));

    return;
  }

  async function getDownload(email: string) {
    const response = await GetReportRun(
      {
        report_id: state.id,
        type: "csv",
        date_range: state.date_range,
        vendor_ids: state.vendor_ids,
        register_ids: state.register_ids,
        department_ids: state.department_ids,
        category_ids: state.category_ids,
        subcategory_ids: state.subcategory_ids,
        compare_date_range: state.compare_date_range,
        email,
      },
      true,
    );

    if (response.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.001")));
      return;
    }

    if (!email) {
      downloadCsv(response.data as string, reportFilename() + '.csv');
    } else {
      dispatch(showSuccess(response.message));
    }
  }

  async function getPrint(email: string) {
    const printRes = await GetReportRun(
      {
        report_id: state.id,
        type: "print",
        date_range: state.date_range,
        vendor_ids: state.vendor_ids,
        register_ids: state.register_ids,
        department_ids: state.department_ids,
        category_ids: state.category_ids,
        subcategory_ids: state.subcategory_ids,
        compare_date_range: state.compare_date_range,
        email,
      },
      true,
    );

    if (printRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.002")));
      return;
    }

    if (!email) {
      openPrintWindow(printRes.data as string);
    } else {
      dispatch(showSuccess(printRes.message));
    }
  }

  async function getVendors() {
    const vendorRes = await GetVendor(null, true);
    if (vendorRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.003")));
      return;
    }
    setState(prevState => ({ ...prevState, allVendors: vendorRes.data }));
  }

  async function getRegisters() {
    const registerRes = await GetRegisters(null, true);
    if (registerRes.status !== StatusCode.OK) {
      dispatch(showError(t("secure.facility.reports.pages.detailed_sales_by_department.004")));
      return;
    }
    setState(prevState => ({ ...prevState, allRegisters: registerRes.data }));
  }

  async function getDepartments() {
    const departmentRes = await GetDepartments(null, true);
    if (departmentRes.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading departments")));
      return;
    }
    setState(prevState => ({ ...prevState, allDepartments: departmentRes.data }));
  }

  useEffect(() => {
    if (state.dateRangeStart && state.dateRangeEnd) {
      const date_range = formatDateRange(state.dateRangeStart, state.dateRangeEnd);
      setState(prevState => ({ ...prevState, date_range }));
    }
  }, [state.dateRangeStart, state.dateRangeEnd]);

  function setDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeStart: date,
    }));
  }

  function setDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      dateRangeEnd: date,
    }));
  }

  useEffect(() => {
    if (state.compare_date_range_start && state.compare_date_range_end) {
      const compare_date_range = formatDateRange(state.compare_date_range_start, state.compare_date_range_end);
      setState(prevState => ({ ...prevState, compare_date_range }));
    }
  }, [state.compare_date_range_start, state.compare_date_range_end]);

  function setCompareDateRangeStart(date: Date) {
    setState(prevState => ({
      ...prevState,
      compare_date_range_start: date,
    }));
  }

  function setCompareDateRangeEnd(date: Date) {
    setState(prevState => ({
      ...prevState,
      compare_date_range_end: date,
    }));
  }

  function onCheckBoxChange(e: React.ChangeEvent<HTMLInputElement>) {
    const { value, checked, id } = e.target;
    let updateSelectedValues: any[] = [];
    if (state[id]) {
      updateSelectedValues = [...state[id]];
    }
    if (checked) {
      updateSelectedValues.push(Number(value));
    } else {
      const removeIndex = updateSelectedValues?.findIndex(valueId => valueId === Number(value));
      if (removeIndex !== -1) {
        updateSelectedValues.splice(removeIndex, 1);
      }
    }
    setState(prevState => ({ ...prevState, [id]: updateSelectedValues }));
  }

  function handleRemoveAll(id: string) {
    setState(prevState => ({ ...prevState, [id]: [] }));
  }

  const [reportEmail, setReportEmail] = useState({
    handler: null,
    open: false,
  });

  function handleReportEmailSelected(email: string) {
    setState({ ...state, email });
    setReportEmail({
      ...reportEmail,
      open: false,
    });
  }

  function handleReportEmailClose() {
    setReportEmail({ handler: null, open: false });
  }

  function handleAddAllDepartments(id: string) {
    const updatedSelectedDepartments: number[] = [];
    state.allDepartments
      ?.filter(department => department.type === "department")
      .forEach(department => {
        updatedSelectedDepartments.push(department?.id);
      });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedDepartments,
    }));
  }

  function handleAddAllCategories(id: string) {
    const updatedSelectedCategories: number[] = [];
    state.allDepartments
      ?.filter(department => department.type === "category")
      .forEach(category => {
        updatedSelectedCategories.push(category?.id);
      });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedCategories,
    }));
  }

  function handleAddAllSubcategories(id: string) {
    const updatedSelectedSubcategories: number[] = [];
    state.allDepartments
      ?.filter(department => department.type === "subcategory")
      .forEach(subcategory => {
        updatedSelectedSubcategories.push(subcategory?.id);
      });
    setState(prevState => ({
      ...prevState,
      [id]: updatedSelectedSubcategories,
    }));
  }

  useEffect(() => {
    if (reportEmail.open === false && reportEmail.handler !== null) {
      reportEmail.handler(state.email);
    }
  }, [reportEmail.open]);

  const primaryAction = {
    content: t("secure.facility.reports.pages.detailed_sales_by_department.005"),
    action: () => {
      if (reportShouldQueue()) {
        dispatch(showSuccess(
          t('Potential large report detected.'),
          10000,
          t('An email is required to send the report to upon completion.'),
        ));

        setReportEmail({
          open: true,
          handler: getReport,
        });
      } else {
        void getReport(state.email);
      }
    },
  };

  function reportFilename(): string {
    return `${state.title}_${moment(new Date()).format("YYYY-MM-DD").toString()}`
      .replace(/ /g, "_")
      .toLowerCase();
  }

  function reportShouldQueue(): boolean {
    return moment(state.dateRangeEnd).diff(moment(state.dateRangeStart), 'days') >= state.queue_after_days;
  }

  function handleDownload() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getDownload,
      });
    } else {
      void getDownload('');
    }
  }

  function handlePrint() {
    if (reportShouldQueue()) {
      setReportEmail({
        open: true,
        handler: getPrint,
      });
    } else {
      void getPrint('');
    }
  }


  return (
    <Page
      title={t("Compare Sales by Date")}
      primaryAction={primaryAction}
      multipleActionDropdownAction={{
        label: t("secure.facility.reports.pages.detailed_sales_by_department.007"),
        dropdownProps: {
          alignment: "right",
          options: [
            {
              type: "handler",
              label: t("secure.facility.reports.pages.detailed_sales_by_department.008"),
              handler: handleDownload,
              icon: "file-arrow-down",
            },
            {
              type: "handler",
              label: t("secure.facility.reports.pages.detailed_sales_by_department.009"),
              handler: handlePrint,
              icon: "print",
            },
          ],
        },
      }}
      breadcrumbs={[
        {
          prefix: true,
          label: t("secure.facility.reports.pages.detailed_sales_by_department.010"),
          url: "/admin/report",
        },
      ]}
    >
      <Card>
        <Card.Section>
          <div className="report-filters-container">
            <DatePickerInput
              label={t("Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.dateRangeStart}
              setStartingDate={setDateRangeStart}
              endingDate={state.dateRangeEnd}
              setEndingDate={setDateRangeEnd}
            />
            <DatePickerInput
              label={t("Compare Date Range")}
              months={windowSize.width > TABLET_WIDTH ? 2 : 1}
              position="left"
              showQuickOptions={windowSize.width > MOBILE_WIDTH}
              startingDate={state.compare_date_range_start}
              setStartingDate={setCompareDateRangeStart}
              endingDate={state.compare_date_range_end}
              setEndingDate={setCompareDateRangeEnd}
            />
            <FilterDropdown
              label={t("Departments")}
              data={state.allDepartments?.filter(department => department.type === "department")}
              dataLabelProperty={"title"}
              checkBoxId={"department_ids"}
              dataValueProperty="id"
              onCheckBoxChange={onCheckBoxChange}
              onClear={() => handleRemoveAll("department_ids")}
              onAddAll={() => handleAddAllDepartments("department_ids")}
              leftAligned={windowSize.width <= MOBILE_WIDTH}
            />
            {state.department_ids?.length > 0 ? (
              <FilterDropdown
                label={t("Categories")}
                data={state.allDepartments
                  ?.filter(department => department.type === "category")
                  ?.filter(category => state.department_ids?.includes(category.parent_id))}
                dataLabelProperty={"title"}
                checkBoxId={"category_ids"}
                dataValueProperty="id"
                onCheckBoxChange={onCheckBoxChange}
                onClear={() => handleRemoveAll("category_ids")}
                onAddAll={() => handleAddAllCategories("category_ids")}
                leftAligned={windowSize.width <= MOBILE_WIDTH}
              />
            ) : null}
            {state.category_ids?.length > 0 ? (
              <FilterDropdown
                label={t("Subcategories")}
                data={state.allDepartments
                  ?.filter(department => department.type === "subcategory")
                  ?.filter(subcategory => state.category_ids?.includes(subcategory.parent_id))}
                dataLabelProperty={"title"}
                checkBoxId={"subcategory_ids"}
                dataValueProperty="id"
                onCheckBoxChange={onCheckBoxChange}
                onClear={() => handleRemoveAll("subcategory_ids")}
                onAddAll={() => handleAddAllSubcategories("subcategory_ids")}
                leftAligned={windowSize.width <= MOBILE_WIDTH}
              />
            ) : null}
          </div>
        </Card.Section>
      </Card>

      <div className="dynamic-table-container">
        <CompareSalesByDateTable
          data={reportData.rows}
          columns={reportData.columns}
          description={reportData.description}
          compileDate={reportData.compileDate}
        />
      </div>
      <ReportEmailModal
        open={reportEmail.open}
        onEmailSelected={handleReportEmailSelected}
        onClose={handleReportEmailClose}
      />
    </Page>
  );
}
